import styled from '@emotion/styled';
import { localeIds } from 'locals';
import { ApplicationDomain } from 'models/Application';
import { ProvisioningProfileRole } from 'models/Application/enum';
import { Application } from 'models/Application/type';
import { DeviceDetail } from 'models/Device/type';
import React from 'react';
import { FormattedDate, FormattedMessage, FormattedTime } from 'react-intl';
import { Driver } from 'views/components/atoms/Driver';
import { Spacer } from 'views/components/atoms/Spacer';
import { Typography } from 'views/components/atoms/Typography';
import { AppFileInfo } from 'views/components/organisms/AppFileInfo';
import { AppInstall } from 'views/components/organisms/AppInstall';
import { ConfigurationProfileInfo } from 'views/components/organisms/ConfigurationProfileInfo';
import { Color, FontSize, MAX_WIDTH } from 'views/constants';

const CELLULAR_DOWNLOAD_LIMITATION = 100000000;

const AppInstallWrapper = styled.div`
  padding: 16px 0;
  background: ${Color.C6};
`;

const ConfigurationProfileInfoWrapper = styled.div`
  max-width: ${MAX_WIDTH};
  margin: auto;
`;

const AppInfoWrapper = styled.div`
  max-width: ${MAX_WIDTH};
  margin: auto;
`;

const DistributionUpdateDateWrapper = styled.div`
  text-align: end;
`;

interface AppDetailProps {
  deviceDetail: DeviceDetail;
  application: Application;
  startInstall?: boolean;
  startOverwriteInstall?: boolean;
  onInstall(): void;
  onOverwriteInstall(): void;
  onUnsupportedOverwriteInstall(): void;
  installReady: boolean;
  onReInstallProfile(): void;
}

export const AppDetail: React.FC<AppDetailProps> = ({
  deviceDetail,
  application,
  startInstall,
  startOverwriteInstall,
  onInstall,
  onOverwriteInstall,
  onUnsupportedOverwriteInstall,
  installReady,
  onReInstallProfile,
}) => (
  <>
    <AppInstallWrapper>
      <AppInfoWrapper>
        <AppInstall
          iconSrc={application.iconSrc}
          ownerName={application.ownerName}
          bundleIdentifier={application.bundleIdentifier}
          revision={application.revision}
          title={application.name}
          version={application.version}
          writableRole={application.isDeveloper}
          updatedDate={application.updatedDate}
          device={{
            target: application.targetDeviceFamily,
            current: deviceDetail.deviceFamily,
          }}
          osVersion={{
            minimum: application.minimumOsVersion,
            device: deviceDetail.osVersion,
          }}
          teamName={application.provisioningProfile.teamName}
          notice={{
            binaryOverSize:
              application.file.size > CELLULAR_DOWNLOAD_LIMITATION,
            duplicateIcon: startOverwriteInstall,
            enterpriseAcceptProfile:
              (startInstall || startOverwriteInstall) &&
              deviceDetail.enterpriseProvisioningWarningAppears &&
              application.provisioningProfile.role ===
                ProvisioningProfileRole.enterprise,
            adhocEnableDeveloperMode:
              (startInstall || startOverwriteInstall) &&
              ApplicationDomain.isGreater0rEqualToIos16(deviceDetail) &&
              application.provisioningProfile.role ===
                ProvisioningProfileRole.adhoc,
          }}
          onInstall={onInstall}
          onOverwriteInstall={onOverwriteInstall}
          onUnsupportedOverwriteInstall={onUnsupportedOverwriteInstall}
          unsupportedOverwriteInstall={ApplicationDomain.isGreater0rEqualToIos16(
            deviceDetail
          )}
          hideOverwriteInstall={!deviceDetail.overwriteInstallAvailable}
          notRegisteredIphone={
            !ApplicationDomain.isInstallPermitted(application)
          }
          expiredProvisioningProfile={ApplicationDomain.isExpiredProvisioningProfile(
            application
          )}
          unsupportedDevice={
            !ApplicationDomain.isSupportedDevice(application, deviceDetail)
          }
          unsupportedOsVersion={
            !ApplicationDomain.isSupportedOsVersion(application, deviceDetail)
          }
          distribution={application.distribution}
          installReady={installReady}
        />
      </AppInfoWrapper>

      <Spacer size="small" />

      {deviceDetail.updateProfileWarningAppears ? (
        <ConfigurationProfileInfoWrapper>
          <ConfigurationProfileInfo
            osVersion={deviceDetail.osVersion}
            onReInstall={onReInstallProfile}
          />
        </ConfigurationProfileInfoWrapper>
      ) : null}
    </AppInstallWrapper>
    <Driver />

    <Spacer size="large" />

    <AppInfoWrapper>
      <Typography bold color={Color.C3}>
        <FormattedMessage id={localeIds.appDetail.releaseNote} />
      </Typography>
    </AppInfoWrapper>
    <Spacer />
    <Driver />
    <Spacer />
    <AppInfoWrapper>
      {application.releaseNote !== '' ? (
        <Typography color={Color.C2} preWrap>
          {application.releaseNote}
        </Typography>
      ) : null}
      {application.distribution ? (
        <DistributionUpdateDateWrapper>
          <Typography color={Color.C2} size={FontSize.XS}>
            <FormattedMessage
              id={localeIds.appDetail.distributionUpdatedDate}
            />
            {': '}
            <FormattedDate
              value={application.distribution?.updatedDate}
              format="default"
            />{' '}
            <FormattedTime
              value={application.distribution?.updatedDate}
              format="default"
            />
          </Typography>
        </DistributionUpdateDateWrapper>
      ) : null}
    </AppInfoWrapper>

    <Spacer />
    <Driver />

    <Spacer size="large" />

    <AppInfoWrapper>
      <Typography bold color={Color.C3}>
        <FormattedMessage id={localeIds.appDetail.info} />
      </Typography>
    </AppInfoWrapper>
    <Spacer />
    <Driver />

    <Spacer />
    <AppInfoWrapper>
      <AppFileInfo
        file={application.file}
        releaseDate={application.updatedDate}
        ttl={application.provisioningProfile.expirationDate}
        minimumOsVersion={application.minimumOsVersion}
        targetOsVersion={application.targetOsVersion}
        targetedDeviceFamily={application.targetDeviceFamily}
      />
    </AppInfoWrapper>
  </>
);
